/* One contact dialog per page; custom-element lifecycle supports editor re-renders. */
if (!customElements.get('tdt-contact')) {
  customElements.define('tdt-contact', class extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.floating = this.querySelector('.tdtc-float');
      this.frame = null;
      this.schedule = () => {
        if (this.frame !== null) return;
        this.frame = requestAnimationFrame(() => { this.frame = null; this.updateFloating(); });
      };
      window.addEventListener('scroll', this.schedule, { passive: true, signal });
      window.addEventListener('resize', this.schedule, { passive: true, signal });
      this.refreshFooters = () => {
        this.footers = [...document.querySelectorAll('footer, #shopify-section-footer, .shopify-section-group-footer-group')];
        this.observer?.disconnect();
        this.observer = new IntersectionObserver(this.schedule);
        this.footers.forEach(footer => this.observer.observe(footer));
        this.schedule();
      };
      this.refreshFooters();
      document.addEventListener('shopify:section:load', this.refreshFooters, { signal });
      document.addEventListener('shopify:section:unload', this.refreshFooters, { signal });
      document.addEventListener('shopify:section:reorder', this.refreshFooters, { signal });
      document.addEventListener('shopify:section:select', event => {
        if (event.target.contains(this)) this.open();
      }, { signal });
      document.addEventListener('shopify:section:deselect', event => {
        if (event.target.contains(this)) this.dialog.close();
      }, { signal });
      document.addEventListener('click', event => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const trigger = event.target.closest('a, [data-contact-open]');
        if (!trigger || trigger.closest('[data-contact-ignore]') || this.dialog.contains(trigger)) return;
        if (!this.isContactLink(trigger)) return;
        event.preventDefault();
        this.open(trigger);
      }, { capture: true, signal });
      this.querySelector('[data-contact-close]').addEventListener('click', () => this.dialog.close(), { signal });
      let backdropDown = false;
      this.dialog.addEventListener('pointerdown', event => { backdropDown = event.target === this.dialog; }, { signal });
      this.dialog.addEventListener('click', event => {
        if (backdropDown && event.target === this.dialog) this.dialog.close();
        backdropDown = false;
      }, { signal });
      this.dialog.addEventListener('close', () => this.afterClose(), { signal });
      this.dialog.addEventListener('keydown', event => {
        if (event.key !== 'Tab') return;
        const focusable = [...this.dialog.querySelectorAll('a[href], button, input:not([type="hidden"]), select, textarea, [tabindex="0"]')]
          .filter(element => !element.disabled && element.getClientRects().length);
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && (document.activeElement === first || !focusable.includes(document.activeElement))) {
          event.preventDefault(); last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault(); first?.focus();
        }
      }, { signal });
      const interests = [...this.querySelectorAll('[data-interest]')];
      const validateInterests = () => {
        if (interests[0]) interests[0].setCustomValidity(interests.some(input => input.checked) ? '' : this.dataset.interestRequired);
      };
      interests.forEach(input => input.addEventListener('change', validateInterests, { signal }));
      validateInterests();
      // Keep the native Shopify POST and CAPTCHA handlers intact.
      this.openFromHash = () => {
        if (location.hash === '#tdt-contact' || location.hash === '#TdtContactForm') this.open();
      };
      window.addEventListener('hashchange', this.openFromHash, { signal });
      if (document.readyState === 'complete') this.openFromHash();
      else window.addEventListener('load', this.openFromHash, { once: true, signal });
      this.updateFloating();
    }
    isContactLink(link) {
      if (link.hasAttribute('data-contact-open')) return true;
      const href = link.getAttribute('href');
      if (!href) return false;
      if (this.dataset.directLinks === 'true' && /^(mailto:|tel:)/i.test(href)) return true;
      let url;
      try { url = new URL(href, location.href); } catch { return false; }
      if (url.origin !== location.origin) return false;
      if (url.hash === '#tdt-contact' || url.hash === '#contact' || url.hash === '#contacto') return true;
      const normalizePath = path => {
        const root = (this.dataset.rootUrl || '/').replace(/\/$/, '');
        const trimmed = path.replace(/\/$/, '');
        return root && (trimmed === root || trimmed.startsWith(root + '/')) ? trimmed.slice(root.length) : trimmed;
      };
      const path = normalizePath(url.pathname);
      if (/\/pages\/(contact|contacto|contact-us|contactenos|contactanos)$/.test(path)) return true;
      if (this.dataset.contactUrl) {
        const configured = new URL(this.dataset.contactUrl, location.href);
        if (normalizePath(url.pathname) === normalizePath(configured.pathname) && url.search === configured.search && url.hash === configured.hash) return true;
      }
      const label = (link.textContent || '').trim().replace(/\s+/g, ' ').toLocaleLowerCase();
      return /^(contacto|contact|contact us|talk to us|get in touch|let[’']s talk|contacta con nosotros|habla con nosotros|hablemos|contáctanos|contactanos)$/.test(label);
    }
    open(trigger) {
      if (this.dialog.open) return;
      this.returnFocus = trigger || document.activeElement;
      // Close the theme's mobile navigation before placing the modal above it.
      const header = trigger?.closest('tdt-header');
      if (header?.classList.contains('is-drawer-open')) {
        header.toggleDrawer(false);
        this.returnFocus = header.burger;
      }
      this.dialog.showModal();
      document.documentElement.classList.add('tdtc-lock');
      this.dialog.scrollTop = 0;
      const initialFocus = this.querySelector('[data-contact-result]') || this.querySelector('#TdtContactTitle');
      initialFocus.focus({ preventScroll: true });
      // Fragment navigation can move focus after load; restore it on the next frame.
      requestAnimationFrame(() => { if (this.isConnected && this.dialog.open) initialFocus.focus({ preventScroll: true }); });
      this.updateFloating();
    }
    afterClose() {
      document.documentElement.classList.remove('tdtc-lock');
      this.updateFloating();
      if (this.returnFocus?.isConnected && this.returnFocus.getClientRects().length) this.returnFocus.focus({ preventScroll: true });
    }
    updateFloating() {
      if (!this.floating) return;
      const footerVisible = (this.footers || []).some(footer => {
        const rect = footer.getBoundingClientRect();
        return rect.height > 0 && rect.top < window.innerHeight && rect.bottom > 0;
      });
      this.floating.hidden = this.dialog.open || window.scrollY <= Number(this.dataset.threshold || 0) || footerVisible;
    }
    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      document.documentElement.classList.remove('tdtc-lock');
      this.controller?.abort();
      this.observer?.disconnect();
      if (this.frame !== null) cancelAnimationFrame(this.frame);
    }
  });
}
